import { inject, Injectable } from "@angular/core";
import { StreamingHubService } from "@pcp/streaming";
import { PcpAuthService } from "@pcp/auth";
import { HubConnection } from "@microsoft/signalr";
import { Endpoint } from "@pcp/http";
import { ClassroomPresenceFacade } from "shared/classroom-presence/data-access";
import { SwPush } from "@angular/service-worker";
import { WebPushHelperService } from "shared/service-worker/feature-web-push";
import { lastValueFrom, take } from "rxjs";
import { ScreenWakeLockService } from "shared/util-screen";
import { PCP_LOGGER } from "@pcp/logging";
import { LanguageConfigService } from "shared/i18n";

@Injectable()
export class PcpDevtoolsService {
    private readonly log = inject(PCP_LOGGER).createScoped('devtools');
    private readonly signalrService = inject(StreamingHubService);
    private readonly authService = inject(PcpAuthService);
    private readonly endpoint = inject(Endpoint);
    private readonly wakeLockService = inject(ScreenWakeLockService);
    private readonly classroomPresence = inject(ClassroomPresenceFacade, { optional: true });

    private readonly languageConfig = inject(LanguageConfigService);

    private readonly swPush = inject(SwPush);
    private readonly webPushHelperService = inject(WebPushHelperService);

    public getSignalrConnection = (): null | HubConnection => this.signalrService.connection;

    readonly signalr = {

        startConnection: () => this.signalrService.startConnection(),
        stopConnection: () => this.signalrService.stopConnection(),
        joinLesson: (lessonId: string) => this.signalrService.joinLesson(lessonId),
        leaveLesson: () => this.signalrService.leaveLesson(),

        /**
         * for more internal SignalR methods
         *
         * @see https://github.com/dotnet/aspnetcore/blob/main/src/SignalR/clients/ts/signalr/src/HubConnection.ts#L314
         * @see @microsoft/signalr#HubConnection
         */
        throwDisconnectError: async (error: any) => {
            await (this.getSignalrConnection() as any)._stopInternal(error);
        },

        reconnect: async () => {
                // NOTE: there are different ways to simulate a reconnect.
                // The `_reconnect` method itself does _not_ work when using Azure SignalR.
                // Closing the underlying WebSocket connection will make the SignalR library reconnect to the server.

                // await (this.getSignalrConnection() as any)._reconnect();
                // await (this.getSignalrConnection() as any)._stopInternal();

                // NOTE: the connection itself has another reference to a connection object
                await (this.getSignalrConnection() as any).connection.transport._webSocket.close();
                this.log.info('new connection id', { connectionId: this.signalrService.connection.connectionId ?? 'unknown' });
            },

        reconnectAndLogout: async () => {
                (this.getSignalrConnection() as any)._reconnect().then(); // do not await

                setTimeout(() => {
                    this.authService.logOut();
                }, 1000);
            },

        fetchUsersInClassroom: async () => this.classroomPresence?.loadClassroomPresence(),

        /**
         * Log-out first and then reconnect (almost at the same time).
         * In this special case, the reconnection info dialog would be shown
         * just a moment after the user gets logged out and redirected to the login page.
         *
         * The app should handle this case and close the dialog immediately.
         */
        logoutFirstThenReconnect: async () => {
            this.authService.logOut();
            (this.getSignalrConnection() as any)._reconnect().then();
        },
    };

    readonly auth = {
        refreshAuthToken: () => {
            this.authService.refreshToken().subscribe();
        },

        logout: () => {
            this.authService.logOut();
        },
    };

    readonly requests = {
        fetchModules: (parallel = 1) => {
            for (let i = 0; i < parallel; i++) {
                this.endpoint.get('/api/module').subscribe();
            }
        },

        fetchContentFull: (parallel = 1) => {
            for (let i = 0; i < parallel; i++) {
                this.endpoint.get('/api/content/202/231/100080/full').subscribe();
            }
        }
    }

    readonly serviceWorker = {
        sendTestPushMessage: () => {
            this.endpoint.post('/api/push/sendNotification', {}).subscribe();
        },
        isPushEnabled: () => this.swPush.isEnabled,
        isWebPushAvailable: () => this.webPushHelperService.pushManagerEnabled(),
        permissionGranted: () => this.webPushHelperService.permissionState(),
        requestPushSubscription: () => this.webPushHelperService.requestAndSaveSubscription(),
        unsubscriptFromPush: () => this.swPush.unsubscribe(),
        hasSubscription: () => lastValueFrom(this.swPush.subscription.pipe(take(1))),
    }

    readonly wakeLock = {
        request: () => this.wakeLockService.requestLock(),
        release: () => this.wakeLockService.releaseLock(),
        isActive: () => this.wakeLockService.isWakeLockActive(),
        getWakeLock: () => this.wakeLockService.getWakeLock(),
    }

    /**
     * Switch the UI language at runtime, to compare a screen in two languages without losing its
     * state. The choice is persisted, so a reload keeps it - use `reset()` to get back to German.
     *
     * Not available in the TUT apps: they do not register these devtools. There, set the
     * `config.lang` localStorage key by hand and reload.
     */
    readonly language = {
        current: () => this.languageConfig.current(),
        available: () => this.languageConfig.available,
        set: (lang: string) => this.languageConfig.setLanguage(lang),
        reset: () => this.languageConfig.reset(),
    }
}
